import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type AdminTextTooltipProps = {
  /** 完整內容；空字串／null／undefined 時不啟用截斷與提示，直接顯示 fallback。 */
  text: string | null | undefined;
  /** 沒有內容時顯示的替代文字。 */
  fallback?: ReactNode;
  /** 套用在截斷文字本身的額外樣式（例如粗體、顏色），會疊加在預設的截斷／底線樣式之上。 */
  className?: string;
};

/**
 * 表格欄位文字過長時，畫面上截斷顯示省略號，滑鼠移過去（桌面）或點一下（觸控裝置，見 onClick 與
 * 外部點擊自動關閉的處理）用浮動氣泡顯示完整內容，取代原生 title 屬性那種樣式無法客製、且在小螢幕
 * 完全沒有觸控對應方式的陽春工具提示。
 *
 * 用 createPortal 掛到 document.body 並用 position: fixed 自行計算座標，是因為這個元件通常用在
 * AdminRefunds／AdminOrderDetail 這類外層包了 overflow-auto／overflow-x-auto 的可捲動表格容器裡——
 * 如果只用一般的 CSS absolute 定位，氣泡會被外層容器的 overflow 裁切掉一部分甚至完全看不到，
 * 這點只能靠跳出容器本身的 DOM 結構（portal + fixed）才能解決。
 */
export function AdminTextTooltip({ text, fallback = "-", className = "" }: AdminTextTooltipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  function show() {
    setOpen(true);
  }
  function hide() {
    setOpen(false);
  }

  // 表格內部或整個頁面捲動、或視窗尺寸改變（RWD 轉向／縮放）時，直接關閉氣泡而不是即時跟著重新定位——
  // 座標是浮動氣泡開啟當下才計算的，捲動後觸發點位置已經不對，與其做複雜的即時追蹤，不如直接收起，
  // 使用者移開滑鼠或再次觸碰即可重新開啟，體感上也更乾脆不會有氣泡「飄移」的違和感。
  useEffect(() => {
    if (!open) return;
    function handleScrollOrResize() {
      hide();
    }
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  // 觸控裝置沒有滑鼠移開事件，靠點擊觸發顯示／收起；點擊氣泡以外的任何地方視為收起。
  useEffect(() => {
    if (!open) return;
    function handleOutsidePointer(event: PointerEvent) {
      if (triggerRef.current?.contains(event.target as Node)) return;
      if (tooltipRef.current?.contains(event.target as Node)) return;
      hide();
    }
    document.addEventListener("pointerdown", handleOutsidePointer);
    return () => document.removeEventListener("pointerdown", handleOutsidePointer);
  }, [open]);

  // 氣泡實際渲染出來後才量得到寬高，所以定位分兩步：先掛上（寬高未知時暫放在 0,0），量完再夾在視窗範圍內，
  // 避免小螢幕（RWD）貼著邊緣時被切掉，並且優先往上顯示、上方空間不夠時自動改往下，貼近一般工具提示的直覺行為。
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;

    const left = Math.max(margin, Math.min(triggerRect.left, viewportWidth - tooltipRect.width - margin));

    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const placeBelow = spaceAbove < tooltipRect.height + margin && spaceBelow > spaceAbove;
    const top = placeBelow ? triggerRect.bottom + 6 : triggerRect.top - tooltipRect.height - 6;

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${Math.max(margin, top)}px`;
  }, [open, text]);

  if (!text) {
    return <span className={className}>{fallback}</span>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        className={`block w-full cursor-help truncate underline decoration-dotted decoration-[#c3cdc7] underline-offset-2 ${className}`}
        onBlur={hide}
        onClick={() => setOpen((current) => !current)}
        onFocus={show}
        onMouseEnter={show}
        onMouseLeave={hide}
        tabIndex={0}
      >
        {text}
      </span>
      {open &&
        createPortal(
          <div
            className="fixed z-[200] max-w-[min(320px,calc(100vw-16px))] whitespace-normal break-words rounded-md border border-[#2c4d3e] bg-[#203d32] px-3 py-2 text-xs font-normal leading-5 text-white shadow-[0_10px_24px_rgba(32,61,50,.3)]"
            ref={tooltipRef}
            role="tooltip"
            style={{ top: 0, left: 0 }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}
